import { useState, useMemo } from "react";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { MagnifyingGlass, Plus } from "@phosphor-icons/react";
import { useAssets, useAssetCategories } from "hooks/useAssets";
import { Asset, AssetCategory } from "shared/types/asset";
import { BuildingAssetCategoryGroup } from "./BuildingAssetCategoryGroup";
import { AddAssetDialog } from "./AddAssetDialog";
import { AssetQuickEditSheet } from "./AssetQuickEditSheet";

interface BuildingAssetsTabProps {
  buildingId: string;
  unitId?: string;
  units?: { id: string; boxNumber?: string }[];
}

export const BuildingAssetsTab = ({
  buildingId,
  unitId,
  units = [],
}: BuildingAssetsTabProps) => {
  const {
    assets,
    isLoading: assetsLoading,
    refetch: refetchAssets,
  } = useAssets({ buildingId, ...(unitId ? { unitId } : {}) });
  const { categories, isLoading: categoriesLoading, refetch: refetchCategories } = useAssetCategories();

  const [search, setSearch] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const filteredAssets = useMemo(() => {
    if (!search) return assets;
    const lower = search.toLowerCase();
    return assets.filter(
      (a) =>
        a.name.toLowerCase().includes(lower) ||
        a.assetCategory?.name.toLowerCase().includes(lower) ||
        a.location?.toLowerCase().includes(lower)
    );
  }, [assets, search]);

  const groupedByCategory = useMemo(() => {
    const map = new Map<number, Asset[]>();
    filteredAssets.forEach((asset) => {
      const catId = asset.assetCategoryId;
      if (!map.has(catId)) map.set(catId, []);
      map.get(catId)!.push(asset);
    });
    return map;
  }, [filteredAssets]);

  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => {
      const aCount = groupedByCategory.get(a.id)?.length || 0;
      const bCount = groupedByCategory.get(b.id)?.length || 0;
      if (aCount > 0 && bCount === 0) return -1;
      if (aCount === 0 && bCount > 0) return 1;
      return a.displayOrder - b.displayOrder;
    });
  }, [categories, groupedByCategory]);

  const totalAssets = assets.length;
  const totalCategories = new Set(assets.map((a) => a.assetCategoryId)).size;

  const handleAssetClick = (asset: Asset) => {
    setSelectedAsset(asset);
    setEditDialogOpen(true);
  };

  const handleAddClick = (_category?: AssetCategory) => {
    setAddDialogOpen(true);
  };

  const isLoading = assetsLoading || categoriesLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-muted-foreground">
          Voorzieningen laden...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Voorzieningen</h2>
          <p className="text-sm text-muted-foreground">
            {totalAssets} voorzieningen | {totalCategories} categorie
            {totalCategories !== 1 ? "en" : ""}
          </p>
        </div>
        <Button onClick={() => handleAddClick()}>
          <Plus className="mr-1 h-4 w-4" />
          Toevoegen
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Zoek in voorzieningen..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Category Groups */}
      <div className="space-y-3">
        {sortedCategories.map((category) => (
          <BuildingAssetCategoryGroup
            key={category.id}
            category={category}
            assets={groupedByCategory.get(category.id) || []}
            onAssetClick={handleAssetClick}
            onAddClick={handleAddClick}
          />
        ))}
      </div>

      {sortedCategories.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-sm text-muted-foreground">
            Nog geen voorzieningen geconfigureerd.
          </p>
          <p className="text-xs text-muted-foreground">
            Ga naar Instellingen &gt; Voorzieningen om categorien en types in te
            stellen.
          </p>
        </div>
      )}

      {/* Dialogs */}
      <AddAssetDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        categories={categories}
        buildingId={buildingId}
        unitId={unitId}
        units={units}
        onSuccess={() => { refetchAssets(); refetchCategories(); }}
      />

      <AssetQuickEditSheet
        asset={selectedAsset}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={refetchAssets}
      />
    </div>
  );
};
